import { useMutation, useQuery } from '@tanstack/react-query';
import { wordApi } from '@/api/word.api';
import { readPhraseWorkbook } from '@/lib/phrase-workbook';
import type { ExcelPhraseFormValue, ExcelPhraseRow } from './excel-phrase.types';

const readPhrases = async (fileId: number): Promise<ExcelPhraseRow[]> => {
  const { blob } = await wordApi.downloadFile(fileId);
  return readPhraseWorkbook(await blob.arrayBuffer()).map((row, index) => ({
    ...row,
    key: `${index}:${row.local}:${row.fr}`,
  }));
};

export function useExcelFilePhrases(fileId: number, isOpen: boolean) {
  const query = useQuery({
    queryKey: ['excel-file-phrases', fileId],
    queryFn: () => readPhrases(fileId),
    enabled: isOpen,
  });
  const addMutation = useMutation({
    mutationFn: (phrase: ExcelPhraseFormValue) => wordApi.addPhrase(fileId, {
      local: phrase.local.trim(),
      fr: phrase.fr.trim(),
      ...(phrase.domain.trim() ? { domain: phrase.domain.trim() } : {}),
      ...(phrase.source.trim() ? { source: phrase.source.trim() } : {}),
    }),
  });
  const removeMutation = useMutation({
    mutationFn: (phrases: ExcelPhraseRow[]) => wordApi.removePhrases(
      fileId,
      phrases.map(({ local, fr }) => ({ local, fr })),
    ),
  });
  const importMutation = useMutation({
    mutationFn: (file: File) => wordApi.importPhrases(fileId, file),
  });

  return {
    phrases: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    reload: query.refetch,
    addPhrase: addMutation.mutateAsync,
    removePhrases: removeMutation.mutateAsync,
    importPhrases: importMutation.mutateAsync,
    isAdding: addMutation.isPending,
    isRemoving: removeMutation.isPending,
    isImporting: importMutation.isPending,
  };
}
